import { Link } from "react-router-dom";
import { PawPrint } from "lucide-react";

const UPDATED = "February 25, 2026";

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="font-heading text-xl sm:text-2xl font-semibold text-ink">{title}</h2>
      <div className="text-sm text-muted-foreground leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Terms() {
  return (
    <div className="min-h-screen bg-sand text-ink">
      <header className="border-b border-border bg-white/60 backdrop-blur">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="terms-home">
            <PawPrint className="w-6 h-6 text-fern" strokeWidth={1.5} />
            <span className="font-heading text-lg font-semibold">Shelter Focus</span>
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-ink">Sign in</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16 space-y-10">
        <div>
          <p className="label-caps mb-2">Legal</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-3">Terms of Service</h1>
          <p className="text-sm text-muted-foreground">Last updated: {UPDATED}</p>
        </div>

        <Section title="Acceptance">
          <p>
            By creating an account or using Shelter Focus (&quot;the app&quot;, &quot;the service&quot;), you agree to these
            Terms of Service and to our <Link to="/privacy" className="text-fern underline">Privacy Policy</Link>.
            If you don&apos;t agree, please don&apos;t use the app.
          </p>
        </Section>

        <Section title="Who can use Shelter Focus">
          <p>
            You must be at least 13 years old to create your own account. Shelters may onboard younger volunteers
            through a parent- or guardian-managed account. You must provide accurate contact information and keep
            your login credentials confidential.
          </p>
        </Section>

        <Section title="Volunteer responsibilities">
          <ul className="list-disc list-inside space-y-1">
            <li>Only sign up for shifts you intend to keep. If you can&apos;t make it, cancel through the app so the slot re-opens.</li>
            <li>Check in and out honestly. Volunteer hours are used for shelter grants, reports, and Galaxy Digital records.</li>
            <li>Report animal issues truthfully. Do not upload photos that identify a person without their consent.</li>
            <li>Be respectful in bulletins and any other messages. Harassment, hate speech, or spam are grounds for account suspension.</li>
            <li>Follow the shelter&apos;s in-person policies (safety training, animal handling rules, dress code). The app does not replace shelter policy.</li>
          </ul>
        </Section>

        <Section title="Shelter admin responsibilities">
          <ul className="list-disc list-inside space-y-1">
            <li>Ensure volunteers have completed any required real-world training before granting shift access, especially for Woof Pack (approval-only) roles.</li>
            <li>Review issue reports promptly and follow up on animal welfare concerns.</li>
            <li>Keep API keys (Galaxy Digital, ShelterLuv, Resend) secret. Rotate keys if they leak.</li>
            <li>Send launch and reset emails only to volunteers who consented to receive them.</li>
          </ul>
        </Section>

        <Section title="Acceptable use">
          <p>You agree not to:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Reverse-engineer, scrape, or automate the app in ways that disrupt other users.</li>
            <li>Upload malicious files, viruses, or content that infringes anyone&apos;s copyright or privacy.</li>
            <li>Attempt to access accounts, data, or shelter instances that don&apos;t belong to you.</li>
            <li>Use Shelter Focus to send unsolicited email, sell products, or run political campaigns.</li>
          </ul>
        </Section>

        <Section title="Content ownership">
          <p>
            You retain ownership of any content you upload (photos, notes, bulletin posts). By posting, you
            grant your shelter and Shelter Focus a non-exclusive license to display, store, and back up that
            content solely to run the service for your shelter.
          </p>
        </Section>

        <Section title="Third-party services">
          <p>
            Shelter Focus integrates with Galaxy Digital, ShelterLuv, and Resend. Your shelter&apos;s use of those
            services is governed by their own terms. We are not responsible for outages, data changes, or
            billing on those platforms.
          </p>
        </Section>

        <Section title="Availability &amp; changes">
          <p>
            We aim for high uptime but do not guarantee uninterrupted service. We may update features, fix bugs,
            or change how integrations work. Material changes to these Terms will be posted here and, where
            appropriate, communicated by email or bulletin.
          </p>
        </Section>

        <Section title="Termination">
          <p>
            Your shelter admin may remove your account at any time. We may suspend accounts that violate these
            Terms. You may stop using the app at any time — ask your admin to delete your record, or email us
            directly.
          </p>
        </Section>

        <Section title="Disclaimers &amp; limitation of liability">
          <p>
            Shelter Focus is provided &quot;as is&quot; without warranties of any kind. To the maximum extent permitted
            by law, we are not liable for indirect, incidental, or consequential damages arising from your use
            of the app. Nothing in these Terms limits liability that cannot be limited by law.
          </p>
        </Section>

        <Section title="Governing law">
          <p>
            These Terms are governed by the laws of the jurisdiction where your shelter operates. Disputes will
            be resolved in the local courts of that jurisdiction unless another venue is agreed in writing.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about these Terms? Email <a href="mailto:hello@shelterfocus.app" className="text-fern underline">hello@shelterfocus.app</a>.
          </p>
        </Section>

        <div className="border-t border-border pt-6 flex flex-wrap gap-4 text-sm">
          <Link to="/privacy" className="text-fern hover:underline">Privacy Policy</Link>
          <Link to="/" className="text-muted-foreground hover:text-ink">Back to home</Link>
        </div>
      </main>
    </div>
  );
}
